/*
 *   File : manage-visibility.js
 *   Author : https://evoqins.com
 *   Description : Manage which mutual funds are visible in the customer app
 *                 (toggles customer.mutual_fund.is_listed via the admin API).
 *                 Independent of fp_active (FP's own enable/disable) and
 *                 is_active (catalog hygiene) — purely customer-app display.
 *   Version : 1.0.0
*/

// Packages
import { Fragment, useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";

// Custom components
import { CustomTitle } from "../../Components/Title";
import { DataTableContainer } from "../../Components/DataTable";
import { Badge } from "../../Components/Badge";
import { CustomSelect, SearchInput } from "../../Components/FormElements";
import { PrimaryButton, SecondaryButton } from "../../Components/Buttons";

// Styles
import Colors from "../../Styles/color.module.scss";

// Services
import APIService from "../../Services/api-service";

const TABLE_STYLE = {
    table: { style: { borderRadius: "0px" } },
    headRow: {
        style: {
            borderBottomColor: "transparent",
            paddingLeft: "24px",
            color: Colors.dark_gray,
            fontFamily: "Montserrat-Bold",
            fontSize: "14px",
            lineHeight: "24px",
            paddingTop: "0px",
            paddingBottom: "0px",
        },
    },
    rows: {
        style: {
            paddingLeft: "24px",
            borderBottom: "none !important",
            "&:nth-child(odd)": { backgroundColor: "#0D0D0D05" },
            "&:hover": { backgroundColor: Colors.lavender_mist },
        },
    },
    cells: {
        style: {
            paddingTop: "12px",
            paddingBottom: "12px",
            color: Colors.dark_charcoal,
            fontFamily: "Montserrat-Medium",
            fontSize: "14px",
            lineHeight: "20px",
            paddingLeft: "0px",
            userSelect: "none",
        },
    },
    headCells: { style: { paddingLeft: "0px" } },
    pagination: {
        style: { paddingRight: "50px", paddingTop: "10px", borderTop: "none" },
    },
};

const LISTED_OPTIONS = [
    { label: "All", value: null },
    { label: "Listed (visible in app)", value: true },
    { label: "Hidden", value: false },
];

const PAGE_SIZE = 25;

const FundVisibility = () => {

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [listedFilter, setListedFilter] = useState(LISTED_OPTIONS[0]);
    const [pageNum, setPageNum] = useState(1);

    const [funds, setFunds] = useState([]);
    const [totalItems, setTotalItems] = useState(0);

    const [tableLoader, setTableLoader] = useState(true);
    const [busyIds, setBusyIds] = useState(new Set());  // rows whose toggle is in-flight
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    // Debounce search input so we don't fire on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        _fetch();
        // eslint-disable-next-line
    }, [pageNum, listedFilter, debouncedSearch]);

    const _fetch = useCallback(() => {
        setTableLoader(true);
        const params = new URLSearchParams();
        params.append("page_num", pageNum);
        params.append("page_size", PAGE_SIZE);
        if (listedFilter.value !== null) params.append("is_listed", listedFilter.value);
        if (debouncedSearch) params.append("search", debouncedSearch);
        const url = `/fund/all-funds?${params.toString()}`;

        APIService(false, url).then(resp => {
            if (resp && resp.status_code === 200) {
                setFunds(resp.data.funds || []);
                setTotalItems(resp.data.total_items || 0);
            } else {
                setFunds([]);
                setTotalItems(0);
                if (resp && resp.message) toast.error(resp.message);
            }
            setTableLoader(false);
            // De-select rows that vanished from this page
            setSelectedIds(prev => {
                const visible = new Set((resp?.data?.funds || []).map(f => f.mf_id));
                return new Set([...prev].filter(id => visible.has(id)));
            });
        });
    }, [pageNum, listedFilter, debouncedSearch]);

    // Optimistic per-row toggle: flip immediately, revert on failure.
    const _toggleOne = (row) => {
        const next = !row.is_listed;
        setBusyIds(prev => new Set([...prev, row.mf_id]));
        setFunds(prev => prev.map(f => f.mf_id === row.mf_id ? { ...f, is_listed: next } : f));

        APIService(true, "/fund/toggle-listing", { mf_id: row.mf_id, is_listed: next }).then(resp => {
            if (!(resp && resp.status_code === 200)) {
                setFunds(prev => prev.map(f => f.mf_id === row.mf_id ? { ...f, is_listed: row.is_listed } : f));
                toast.error(resp?.message || "Failed to update fund visibility");
            } else {
                toast.success(next ? "Fund listed" : "Fund hidden");
            }
            setBusyIds(prev => {
                const n = new Set(prev); n.delete(row.mf_id); return n;
            });
        });
    };

    const _bulk = (target_is_listed) => {
        if (selectedIds.size === 0) {
            toast.info("Select at least one fund");
            return;
        }
        setBulkBusy(true);
        const ids = Array.from(selectedIds);
        APIService(true, "/fund/toggle-listing", { mf_ids: ids, is_listed: target_is_listed }).then(resp => {
            if (resp && resp.status_code === 200) {
                toast.success(resp.message || `${resp.data?.updated || 0} fund(s) updated`);
                setSelectedIds(new Set());
                _fetch();
            } else {
                toast.error(resp?.message || "Bulk update failed");
            }
            setBulkBusy(false);
        });
    };

    const _toggleRowSelect = (mf_id) => {
        setSelectedIds(prev => {
            const n = new Set(prev);
            if (n.has(mf_id)) n.delete(mf_id); else n.add(mf_id);
            return n;
        });
    };

    const _toggleSelectAllOnPage = () => {
        const visible = funds.map(f => f.mf_id);
        const allOn = visible.every(id => selectedIds.has(id));
        setSelectedIds(prev => {
            const n = new Set(prev);
            if (allOn) visible.forEach(id => n.delete(id));
            else visible.forEach(id => n.add(id));
            return n;
        });
    };

    const COLUMNS = [
        {
            name: (
                <input type="checkbox"
                    checked={funds.length > 0 && funds.every(f => selectedIds.has(f.mf_id))}
                    onChange={_toggleSelectAllOnPage} />
            ),
            cell: row => (
                <input type="checkbox"
                    checked={selectedIds.has(row.mf_id)}
                    onChange={() => _toggleRowSelect(row.mf_id)} />
            ),
            width: "60px",
        },
        {
            name: "Fund",
            selector: row => row.fund_name,
            cell: row => (
                <div>
                    <div className="e-montserrat-medium e-font-14">{row.fund_name}</div>
                    <div className="e-montserrat-regular e-font-12 color-dark-gray">
                        {row.amc_name} · {row.asset_class || "—"} · ISIN {row.isin || "—"}
                    </div>
                </div>
            ),
            grow: 3,
        },
        {
            name: "FP",
            cell: row => (
                <Badge status={row.fp_active ? "Active" : "Inactive"}
                    color={row.fp_active ? "#069A6E" : "#9A0664"} />
            ),
            width: "100px",
        },
        {
            name: "Visible in app",
            cell: row => (
                <div className="form-check form-switch m-0">
                    <input className="form-check-input" type="checkbox"
                        role="switch"
                        checked={row.is_listed}
                        disabled={busyIds.has(row.mf_id)}
                        onChange={() => _toggleOne(row)}
                        style={{ cursor: busyIds.has(row.mf_id) ? "not-allowed" : "pointer" }}
                    />
                    <span className="e-montserrat-regular e-font-12 color-dark-gray ms-2">
                        {row.is_listed ? "Listed" : "Hidden"}
                    </span>
                </div>
            ),
            width: "180px",
        },
    ];

    return (
        <Fragment>
            <CustomTitle title="Fund Visibility"
                type="Manage which mutual funds appear in the customer app" />

            <div className="d-flex flex-wrap align-items-end gap-3 mt-3 mb-2 px-3">
                <div style={{ flex: "1 1 320px", minWidth: 280 }}>
                    <SearchInput placeholder="Search fund / AMC name"
                        value={search}
                        valueChange={(v) => { setPageNum(1); setSearch(v); }}
                    />
                </div>

                <div style={{ width: 240 }}>
                    <CustomSelect label="Show"
                        options={LISTED_OPTIONS}
                        value={listedFilter}
                        selectChange={(opt) => { setPageNum(1); setListedFilter(opt); }}
                    />
                </div>

                <div className="d-flex gap-2 ms-auto">
                    <SecondaryButton name={`Hide selected${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
                        loader={bulkBusy}
                        handleClick={() => _bulk(false)}
                    />
                    <PrimaryButton name={`List selected${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
                        loader={bulkBusy}
                        handleClick={() => _bulk(true)}
                    />
                </div>
            </div>

            <div className="px-3 e-montserrat-regular e-font-13 color-dark-gray mb-2">
                {totalItems.toLocaleString()} total · page {pageNum} of {Math.max(1, Math.ceil(totalItems / PAGE_SIZE))}
                {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
            </div>

            <DataTableContainer data={funds}
                columns={COLUMNS}
                customStyles={TABLE_STYLE}
                pagination={true}
                paginationServer={true}
                paginationTotalRows={totalItems}
                paginationPerPage={PAGE_SIZE}
                onChangePage={(p) => setPageNum(p)}
                progressPending={tableLoader}
            />
        </Fragment>
    );
};

export default FundVisibility;
